import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import LoadingSpinner from '../components/shared/LoadingSpinner';

export function useMaintenanceMode() {
    const navigate = useNavigate();
    const { maintenanceMode, setMaintenanceMode, isLoading } = useSettings();
    const { user } = useAuth();
    const { socket } = useWebSocket();

    const handleMaintenanceChange = useCallback((newMaintenanceMode: boolean) => {
        setMaintenanceMode(newMaintenanceMode);
        if (newMaintenanceMode && user?.role !== 'admin') {
            navigate('/maintenance');
        }
    }, [setMaintenanceMode, user, navigate]);

    useEffect(() => {
        if (!socket) return;

        // Écoute des mises à jour de maintenance via WebSocket
        socket.on('maintenanceUpdate', (data: { maintenanceMode: boolean }) => {
            handleMaintenanceChange(data.maintenanceMode);
        });

        // Polling de secours - activé seulement si WebSocket déconnecté
        // Intervalle augmenté à 5 minutes pour réduire la charge serveur
        let pollingInterval: NodeJS.Timeout | null = null;
        let lastETag: string | null = null;

        const startPolling = () => {
            pollingInterval = setInterval(async () => {
                // Ne polluer que si socket déconnecté
                if (socket.connected) {
                    return;
                }

                try {
                    const headers: HeadersInit = {};
                    if (lastETag) {
                        headers['If-None-Match'] = lastETag;
                    }

                    const response = await fetch(
                        `${process.env.REACT_APP_URL_API_MACADAMIA}/settings/public`,
                        { headers }
                    );

                    // 304 = pas de changement
                    if (response.status === 304) {
                        return;
                    }

                    if (response.ok) {
                        const etag = response.headers.get('ETag');
                        if (etag) {
                            lastETag = etag;
                        }

                        const data = await response.json();
                        if (data.maintenanceMode !== maintenanceMode) {
                            handleMaintenanceChange(data.maintenanceMode);
                        }
                    }
                } catch (error) {
                    console.error('Erreur lors du polling maintenance:', error);
                }
            }, 300000); // 5 minutes (300000ms) au lieu de 30 secondes
        };

        startPolling();

        return () => {
            socket.off('maintenanceUpdate');
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [socket, maintenanceMode, handleMaintenanceChange]);

    return { maintenanceMode, isLoading };
}

export const MaintenanceGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const { maintenanceMode, isLoading } = useMaintenanceMode();
    const { user } = useAuth();

    useEffect(() => {
        if (!isLoading && maintenanceMode && user?.role !== 'admin') {
            navigate('/maintenance');
        }
    }, [maintenanceMode, isLoading, user, navigate]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return <>{children}</>;
};